"use server"

import { sdk } from "@lib/config"
import { HttpTypes } from "@medusajs/types"
import { revalidateTag } from "next/cache"
import {
  getAuthHeaders,
  getCacheTag,
  getCartId,
  removeCartId,
} from "./cookies"
import { getRegion } from "./regions"
import { getLocale } from "./locale-actions"

/**
 * Express checkout ("Comprar ya" and quick pay from the cart) without an
 * account and without the multi-step checkout:
 *
 * - Apple Pay / Google Pay: the MONEI PaymentRequest component collects the
 *   shipping address in the wallet sheet. These actions quote Packlink for the
 *   (redacted) address while the sheet is open and then pay with the wallet
 *   token, creating the MONEI payment server side.
 * - Bizum / card: Bizum does not share any address, so a short guest form
 *   collects it and the customer is sent to the MONEI hosted payment page.
 *
 * "Comprar ya" uses its own cart so the customer's basket is left untouched.
 */

const MONEI_PROVIDER_ID = "pp_monei_monei"

// MONEI works in minor units, Medusa totals are decimal amounts.
const toMinor = (amount: number | null | undefined) =>
  Math.round(Number(amount ?? 0) * 100)

export type ExpressShippingOption = {
  id: string
  label: string
  description?: string
  amount: number
}

export type ExpressQuote = {
  shippingOptions: ExpressShippingOption[]
  /** Cart total in minor units with the first option selected */
  amount: number
  error?: string
}

/** Address / contact details as returned by the MONEI wallet components */
export type WalletDetails = {
  name?: string
  email?: string
  phone?: string
  company?: string
  address?: {
    country?: string
    city?: string
    line1?: string
    line2?: string
    zip?: string
    state?: string
  }
}

export type ExpressResult =
  | { ok: true; orderUrl: string }
  | { ok: true; redirectUrl: string }
  | { ok: false; error: string }

const revalidateCart = async () => {
  revalidateTag(await getCacheTag("carts"))
  revalidateTag(await getCacheTag("fulfillment"))
}

const headers = async () => ({ ...(await getAuthHeaders()) })

const errorMessage = (e: unknown) =>
  e instanceof Error ? e.message : String(e)

async function fetchCart(cartId: string) {
  const { cart } = await sdk.store.cart.retrieve(
    cartId,
    {
      fields:
        "id,total,currency_code,region_id,email,metadata,*shipping_methods,payment_collection.id",
    },
    await headers()
  )
  return cart
}

// Mirrors the Packlink provider rule: no VAT territory, no shipping.
function assertShippableZip(country: string, zip?: string) {
  if (country.toLowerCase() === "es" && /^(35|38|51|52)/.test((zip ?? "").trim())) {
    throw new Error("Por ahora no enviamos a Canarias, Ceuta ni Melilla.")
  }
}

async function assertShippableCountry(countryCode: string, country: string) {
  const region = await getRegion(countryCode)
  const iso = country.toLowerCase()
  if (!region?.countries?.some((c) => c.iso_2 === iso)) {
    throw new Error(`Lo sentimos, no enviamos a ${country.toUpperCase()}.`)
  }
  return iso
}

/** Countries of the current region, for the guest quick-buy form. */
export async function listExpressCountries(countryCode: string) {
  const region = await getRegion(countryCode)
  return (region?.countries ?? []).map((c) => ({
    iso_2: c.iso_2 ?? "",
    name: c.display_name ?? c.iso_2?.toUpperCase() ?? "",
  }))
}

/**
 * Creates the dedicated cart used by "Comprar ya" with a single product.
 * It is not stored in the cart cookie, so the basket is not replaced.
 */
export async function createBuyNowCart({
  countryCode,
  variantId,
  quantity,
}: {
  countryCode: string
  variantId: string
  quantity: number
}): Promise<{ cartId: string; amount: number; currency: string }> {
  const region = await getRegion(countryCode)
  if (!region) {
    throw new Error(`Region not found for country code: ${countryCode}`)
  }
  const locale = await getLocale()
  const { cart } = await sdk.store.cart.create(
    {
      region_id: region.id,
      locale: locale || undefined,
      items: [{ variant_id: variantId, quantity }],
      metadata: { express_checkout: "buy_now" },
    },
    { fields: "id,total,currency_code" },
    await headers()
  )
  return {
    cartId: cart.id,
    amount: toMinor(cart.total),
    currency: cart.currency_code.toUpperCase(),
  }
}

/**
 * Sets the (possibly partial) shipping address and returns the shipping
 * options Packlink offers for it, cheapest first. The first option is applied
 * to the cart so the returned amount is the real total.
 */
export async function quoteExpressShipping({
  cartId,
  countryCode,
  address,
}: {
  cartId: string
  countryCode: string
  address: { country?: string; zip?: string; city?: string; state?: string }
}): Promise<ExpressQuote> {
  try {
    if (!address.country || !address.zip) {
      throw new Error("Indica el pais y el codigo postal de entrega.")
    }
    const country = await assertShippableCountry(countryCode, address.country)
    assertShippableZip(country, address.zip)
    const h = await headers()

    await sdk.store.cart.update(
      cartId,
      {
        shipping_address: {
          country_code: country,
          postal_code: address.zip,
          city: address.city || undefined,
          province: address.state || undefined,
        },
      },
      {},
      h
    )

    const { shipping_options } = await sdk.client.fetch<{
      shipping_options: HttpTypes.StoreCartShippingOption[]
    }>("/store/shipping-options", { query: { cart_id: cartId }, headers: h })

    const priced = await Promise.all(
      shipping_options
        .filter(
          (o) =>
            !(o as { insufficient_inventory?: boolean }).insufficient_inventory
        )
        .map(async (o): Promise<ExpressShippingOption | null> => {
          let amount = o.amount as number | undefined
          if (o.price_type === "calculated") {
            amount = await sdk.client
              .fetch<{ shipping_option: HttpTypes.StoreCartShippingOption }>(
                `/store/shipping-options/${o.id}/calculate`,
                { method: "POST", body: { cart_id: cartId }, headers: h }
              )
              .then(({ shipping_option }) => shipping_option.amount as number)
              .catch(() => undefined)
          }
          return amount === undefined || amount === null
            ? null
            : {
                id: o.id,
                label: o.name.replace(/\s*\(Packlink\)\s*/i, ""),
                description: amount === 0 ? "Envio gratis" : undefined,
                amount: toMinor(amount),
              }
        })
    )
    const options = priced
      .filter((o): o is ExpressShippingOption => !!o)
      .sort((a, b) => a.amount - b.amount)

    if (!options.length) {
      throw new Error("No hay envios disponibles para esta direccion.")
    }

    await sdk.store.cart.addShippingMethod(
      cartId,
      { option_id: options[0].id },
      {},
      h
    )
    const cart = await fetchCart(cartId)
    await revalidateCart()

    return { shippingOptions: options, amount: toMinor(cart.total) }
  } catch (e) {
    return { shippingOptions: [], amount: 0, error: errorMessage(e) }
  }
}

/** Applies the shipping option chosen in the wallet sheet. */
export async function selectExpressShipping({
  cartId,
  optionId,
}: {
  cartId: string
  optionId: string
}): Promise<{ amount: number }> {
  await sdk.store.cart.addShippingMethod(
    cartId,
    { option_id: optionId },
    {},
    await headers()
  )
  const cart = await fetchCart(cartId)
  await revalidateCart()
  return { amount: toMinor(cart.total) }
}

const splitName = (name?: string) => {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean)
  return {
    first_name: parts[0] ?? "",
    last_name: parts.slice(1).join(" "),
  }
}

const toCartAddress = (
  details: WalletDetails,
  fallback?: WalletDetails
): HttpTypes.StoreAddAddress => ({
  ...splitName(details.name ?? fallback?.name),
  company: details.company || undefined,
  address_1: details.address?.line1 ?? "",
  address_2: details.address?.line2 || undefined,
  postal_code: details.address?.zip ?? "",
  city: details.address?.city ?? "",
  province: details.address?.state || undefined,
  country_code: details.address?.country?.toLowerCase(),
  phone: details.phone || fallback?.phone || undefined,
})

async function applyShipping(
  cartId: string,
  shippingOptionId: string | undefined
) {
  // Changing the address can drop the shipping method, so apply it again.
  const cart = await fetchCart(cartId)
  const optionId =
    shippingOptionId ?? cart.shipping_methods?.at(-1)?.shipping_option_id
  if (!optionId) {
    throw new Error("Selecciona un metodo de envio.")
  }
  await sdk.store.cart.addShippingMethod(
    cartId,
    { option_id: optionId },
    {},
    await headers()
  )
}

async function completeExpressCart(cartId: string): Promise<ExpressResult> {
  const res = await sdk.store.cart.complete(cartId, {}, await headers())
  await revalidateCart()

  if (res.type !== "order") {
    return {
      ok: false,
      error: res.error?.message ?? "No se ha podido crear el pedido.",
    }
  }

  revalidateTag(await getCacheTag("orders"))
  // Only forget the basket when the basket itself was bought.
  if ((await getCartId()) === cartId) {
    await removeCartId()
  }
  const country = res.order.shipping_address?.country_code?.toLowerCase()
  return { ok: true, orderUrl: `/${country}/order/${res.order.id}/confirmed` }
}

/**
 * Pays with an Apple Pay / Google Pay token from the MONEI PaymentRequest
 * component and places the order.
 */
export async function payWithWallet({
  cartId,
  countryCode,
  token,
  moneiSessionId,
  finalAmount,
  shippingOptionId,
  shippingDetails,
  billingDetails,
}: {
  cartId: string
  countryCode: string
  token: string
  moneiSessionId: string
  finalAmount?: number
  shippingOptionId?: string
  shippingDetails?: WalletDetails
  billingDetails?: WalletDetails
}): Promise<ExpressResult> {
  try {
    if (!shippingDetails?.address?.line1) {
      throw new Error("El monedero no ha enviado la direccion de entrega.")
    }
    const email = shippingDetails.email || billingDetails?.email
    if (!email) {
      throw new Error("El monedero no ha enviado un email de contacto.")
    }
    await assertShippableCountry(
      countryCode,
      shippingDetails.address.country ?? ""
    )

    const shippingAddress = toCartAddress(shippingDetails, billingDetails)
    const billingAddress = billingDetails?.address?.line1
      ? toCartAddress(billingDetails, shippingDetails)
      : shippingAddress

    await sdk.store.cart.update(
      cartId,
      {
        email,
        shipping_address: shippingAddress,
        billing_address: billingAddress,
      },
      {},
      await headers()
    )
    await applyShipping(cartId, shippingOptionId)

    const cart = await fetchCart(cartId)
    // The wallet authorised finalAmount; never charge anything else.
    if (finalAmount !== undefined && toMinor(cart.total) !== finalAmount) {
      throw new Error(
        "El importe del pedido ha cambiado. Revisa la cesta e intentalo de nuevo."
      )
    }

    const resp = await sdk.store.payment.initiatePaymentSession(
      cart,
      {
        provider_id: MONEI_PROVIDER_ID,
        data: {
          cart_id: cartId,
          country_code: countryCode,
          email,
          express: true,
          payment_token: token,
          monei_session_id: moneiSessionId,
          shipping_details: shippingDetails,
          billing_details: billingDetails ?? shippingDetails,
        },
      },
      {},
      await headers()
    )
    const session = resp.payment_collection?.payment_sessions?.find(
      (s) => s.provider_id === MONEI_PROVIDER_ID
    )
    const data = (session?.data ?? {}) as Record<string, unknown>

    if (data.status === "FAILED" || data.status === "CANCELED") {
      throw new Error(
        (data.status_message as string) || "El pago ha sido rechazado."
      )
    }
    // Some cards still require a 3D Secure challenge on MONEI.
    if (data.status === "PENDING" && data.redirect_url) {
      return { ok: true, redirectUrl: data.redirect_url as string }
    }

    return await completeExpressCart(cartId)
  } catch (e) {
    return { ok: false, error: errorMessage(e) }
  }
}

export type GuestCheckoutInput = {
  cartId: string
  countryCode: string
  shippingOptionId: string
  /** "bizum" limits the MONEI page to Bizum, "all" shows every method */
  method: "bizum" | "all"
  email: string
  firstName: string
  lastName: string
  phone: string
  address1: string
  postalCode: string
  city: string
  province?: string
  country: string
  /** Optional NIF/CIF for a full invoice, plus the company name */
  taxId?: string
  company?: string
}

/**
 * Guest quick buy for Bizum (which does not share an address) and cards:
 * saves the contact and address typed in the short form and returns the MONEI
 * hosted page URL. MONEI sends the customer back to /api/monei/complete.
 */
export async function payAsGuest(
  input: GuestCheckoutInput
): Promise<ExpressResult> {
  try {
    const required = [
      input.email,
      input.firstName,
      input.phone,
      input.address1,
      input.postalCode,
      input.city,
      input.country,
    ]
    if (required.some((v) => !v?.trim())) {
      throw new Error("Completa todos los campos obligatorios.")
    }
    const country = await assertShippableCountry(
      input.countryCode,
      input.country
    )
    assertShippableZip(country, input.postalCode)

    const taxId = input.taxId?.replace(/[\s-]/g, "").toUpperCase()
    if (taxId && !/^[A-Z0-9]{8,12}$/.test(taxId)) {
      throw new Error("El NIF no es valido.")
    }

    const address: HttpTypes.StoreAddAddress = {
      first_name: input.firstName.trim(),
      last_name: input.lastName.trim(),
      address_1: input.address1.trim(),
      postal_code: input.postalCode.trim(),
      city: input.city.trim(),
      province: input.province?.trim() || undefined,
      country_code: country,
      phone: input.phone.trim(),
    }

    const current = await fetchCart(input.cartId)
    await sdk.store.cart.update(
      input.cartId,
      {
        email: input.email.trim(),
        shipping_address: address,
        billing_address: { ...address, company: input.company?.trim() || undefined },
        // The invoice is issued with this NIF (full invoice instead of simplified).
        metadata: { ...(current.metadata ?? {}), tax_id: taxId || null },
      },
      {},
      await headers()
    )
    await applyShipping(input.cartId, input.shippingOptionId)

    const cart = await fetchCart(input.cartId)
    const fullName = `${address.first_name} ${address.last_name}`.trim()
    const resp = await sdk.store.payment.initiatePaymentSession(
      cart,
      {
        provider_id: MONEI_PROVIDER_ID,
        data: {
          cart_id: input.cartId,
          country_code: input.countryCode,
          email: input.email.trim(),
          customer_name: fullName,
          customer_phone: address.phone,
          express: true,
          allowed_payment_methods:
            input.method === "bizum" ? ["bizum"] : undefined,
          shipping_details: {
            name: fullName,
            email: input.email.trim(),
            phone: address.phone,
            address: {
              country: country.toUpperCase(),
              city: address.city,
              line1: address.address_1,
              zip: address.postal_code,
              state: address.province,
            },
          },
        },
      },
      {},
      await headers()
    )
    await revalidateCart()

    const session = resp.payment_collection?.payment_sessions?.find(
      (s) => s.provider_id === MONEI_PROVIDER_ID
    )
    const redirectUrl = session?.data?.redirect_url as string | undefined
    if (!redirectUrl) {
      throw new Error("No se pudo iniciar el pago con MONEI.")
    }
    return { ok: true, redirectUrl }
  } catch (e) {
    return { ok: false, error: errorMessage(e) }
  }
}
